import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { Configuration as V1Configuration } from '../api/generated/v1/configuration-export'
import * as apiV1 from '../api/generated/v1/api'
import { getApiBaseUrl, API_V1_PREFIX } from '../config/constants'

interface AuthContextProps {
	accessToken: string | null
	isAuthenticated: boolean
	isLoading: boolean
	login: (accessToken: string, refreshToken: string) => void
	logout: () => void
	/**
	 * Attempts to refresh access token.
	 * @returns new access token string if successful, otherwise null
	 */
	refreshToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [accessToken, setAccessToken] = useState<string | null>(null)
	const [refreshTokenValue, setRefreshTokenValue] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		const token = localStorage.getItem(ACCESS_TOKEN_KEY)
		const refresh = localStorage.getItem(REFRESH_TOKEN_KEY)
		console.log('AuthContext init:', { token: !!token, refresh: !!refresh })
		if (token) {
			setAccessToken(token)
		}
		if (refresh) {
			setRefreshTokenValue(refresh)
		}
		setIsLoading(false)
	}, [])

	const login = useCallback((accessToken: string, refreshToken: string) => {
		localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
		localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
		setAccessToken(accessToken)
		setRefreshTokenValue(refreshToken)
	}, [])

	const logout = useCallback(() => {
		localStorage.removeItem(ACCESS_TOKEN_KEY)
		localStorage.removeItem(REFRESH_TOKEN_KEY)
		setAccessToken(null)
		setRefreshTokenValue(null)
	}, [])

	const refreshToken = useCallback(async (): Promise<string | null> => {
		if (!refreshTokenValue) {
			logout()
			return null
		}
		try {
			// Create dedicated API client for refresh (no dependency on ApiProvider)
			const apiBaseUrl = getApiBaseUrl()
			const config = new V1Configuration({
				basePath: `${apiBaseUrl}${API_V1_PREFIX}`,
				accessToken: accessToken || undefined,
			})
			const userApi = new apiV1.UserApi(config)
			
			const res = await userApi.refreshTokens({
				access_token: accessToken || undefined,
				refresh_token: refreshTokenValue
			})
			const newAccessToken = res.data.access_token
			const newRefreshToken = res.data.refresh_token
			if (newAccessToken && newRefreshToken) {
				localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken)
				localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken)
				setAccessToken(newAccessToken)
				setRefreshTokenValue(newRefreshToken)
			} else {
				logout()
			}
			return newAccessToken || null
		} catch (e) {
			logout()
			return null
		}
	}, [accessToken, refreshTokenValue, logout])

	return (
		<AuthContext.Provider value={{ accessToken, isAuthenticated: !!accessToken, isLoading, login, logout, refreshToken }}>
			{children}
		</AuthContext.Provider>
	)
}

export function useAuth() {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return context
} 